/**
 * Request Account - Form Page
 *
 * Full account request form after email verification.
 */

import { useState, useEffect, useRef, type FormEvent, type ChangeEvent } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Input } from '@/common/components/input'
import { Button } from '@/common/components/button'
import { Text } from '@/common/components/text'
import { Combobox } from '@/common/components/combobox'
import { Multiselect } from '@/common/components/multiselect'
import { RadioGroup } from '@/common/components/radio-group'
import { Select } from '@/common/components/select'
import { Textarea } from '@/common/components/textarea'
import { useMarkets } from '@/common/hooks/useMarkets'
import { useStatesSource } from '@/common/data/statesSource'
import {
  verifyToken,
  submitAccountRequest,
  uploadPof,
  type RequestedRole,
} from '@/features/account-request/api/accountRequest'
import * as styles from './index.css'

export const Route = createFileRoute({
  component: RequestFormPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || '',
  }),
})

function RequestFormPage() {
  const navigate = useNavigate()
  const { token } = Route.useSearch()

  const [email, setEmail] = useState('')
  const [requestedRole, setRequestedRole] = useState<RequestedRole>('investor')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([])

  // Investor qualification fields
  const [wholesaleIntent, setWholesaleIntent] = useState('')
  const [proofOfFundsAvailable, setProofOfFundsAvailable] = useState('')
  const [pofFileKey, setPofFileKey] = useState<string | null>(null)
  const [pofFileName, setPofFileName] = useState<string | null>(null)
  const [pofUploading, setPofUploading] = useState(false)
  const [pofError, setPofError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [hardMoneyLender, setHardMoneyLender] = useState('')
  const [hardMoneyLenderExplanation, setHardMoneyLenderExplanation] = useState('')
  const [annualPurchaseVolume, setAnnualPurchaseVolume] = useState('')
  const [referralSource, setReferralSource] = useState('')
  const [referralSourceDetail, setReferralSourceDetail] = useState('')

  // Agent qualification fields
  const [licenseNumber, setLicenseNumber] = useState('')
  const [licenseState, setLicenseState] = useState('')
  const [mlsBoardsInput, setMlsBoardsInput] = useState('')
  const [partnershipSource, setPartnershipSource] = useState('')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tokenError, setTokenError] = useState<string | null>(null)

  // Fetch markets using shared hook
  const { groupedMarketOptions } = useMarkets()

  // States dropdown — full name label, 2-letter code value (matches API contract)
  const statesSource = useStatesSource({ label: 'name', value: 'code' })

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setTokenError('No verification token provided. Please start over.')
      return
    }

    verifyToken(token)
      .then((result) => {
        setEmail(result.email)
        setRequestedRole(result.requested_role)
      })
      .catch((err) => {
        setTokenError(err instanceof Error ? err.message : 'Invalid or expired token')
      })
  }, [token])

  const handlePofFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file size client-side
    if (file.size > 10 * 1024 * 1024) {
      setPofError('File too large. Maximum size is 10MB')
      return
    }

    setPofError(null)
    setPofUploading(true)

    try {
      const result = await uploadPof(token, file)
      setPofFileKey(result.file_key)
      setPofFileName(result.file_name)
    } catch (err) {
      setPofError(err instanceof Error ? err.message : 'Failed to upload file')
    } finally {
      setPofUploading(false)
    }
  }

  const handleRemovePofFile = () => {
    setPofFileKey(null)
    setPofFileName(null)
    setPofError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      if (requestedRole === 'agent') {
        const mlsBoards = mlsBoardsInput
          .split(',')
          .map((b) => b.trim())
          .filter(Boolean)
        await submitAccountRequest({
          token,
          requested_role: 'agent',
          first_name: firstName,
          last_name: lastName,
          phone,
          company_name: companyName,
          markets: selectedMarkets,
          qualification_data: {
            license_number: licenseNumber,
            license_state: licenseState,
            mls_boards: mlsBoards,
          },
          partnership_source: partnershipSource.trim() || null,
        })
      } else {
        await submitAccountRequest({
          token,
          requested_role: 'investor',
          first_name: firstName,
          last_name: lastName,
          phone,
          company_name: companyName,
          markets: selectedMarkets,
          wholesale_intent: wholesaleIntent,
          proof_of_funds_available: proofOfFundsAvailable,
          proof_of_funds_file_key: pofFileKey,
          proof_of_funds_file_name: pofFileName,
          hard_money_lender: hardMoneyLender,
          hard_money_lender_explanation:
            hardMoneyLender === 'yes' || hardMoneyLender === 'sometimes'
              ? hardMoneyLenderExplanation
              : null,
          annual_purchase_volume: annualPurchaseVolume,
          referral_source: referralSource,
          referral_source_detail:
            referralSource === 'agent_referral' ||
            referralSource === 'investor_referral' ||
            referralSource === 'other'
              ? referralSourceDetail
              : null,
        })
      }
      navigate({ to: '/request-account/submitted', search: { role: requestedRole } })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Check if error indicates account already exists
  const isAccountExistsError = tokenError?.includes('already exists')

  // Show token error
  if (tokenError) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <img
              src="/images/orchard-offers.svg"
              alt="Orchard Cash Offer"
              className={styles.logo}
            />
            <Text as="h1" variant="h4" weight="bold" className={styles.title}>
              {isAccountExistsError ? 'Account Already Exists' : 'Link Expired or Invalid'}
            </Text>
            <Text variant="body" className={styles.subtitle}>
              {isAccountExistsError
                ? 'An account with this email already exists. Please sign in to access your account.'
                : tokenError}
            </Text>
          </div>

          {isAccountExistsError ? (
            <Link to="/login">
              <Button width="full">Sign In</Button>
            </Link>
          ) : (
            <>
              <Text variant="bodySm" className={styles.description}>
                Please start over to request a new verification link.
              </Text>
              <Link to="/request-account">
                <Button width="full">Start Over</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    )
  }

  // Show loading while verifying token
  if (!email) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <img
              src="/images/orchard-offers.svg"
              alt="Orchard Cash Offer"
              className={styles.logo}
            />
            <Text variant="body" className={styles.subtitle}>Verifying your email...</Text>
          </div>
        </div>
      </div>
    )
  }

  const needsHardMoneyExplanation = hardMoneyLender === 'yes' || hardMoneyLender === 'sometimes'
  const needsReferralDetail = referralSource === 'agent_referral' || referralSource === 'investor_referral' || referralSource === 'other'
  const needsPofFile = proofOfFundsAvailable === 'yes'

  const mlsBoardsList = mlsBoardsInput.split(',').map((b) => b.trim()).filter(Boolean)

  const baseValid =
    firstName.trim() &&
    lastName.trim() &&
    phone.trim() &&
    companyName.trim() &&
    selectedMarkets.length > 0

  const agentValid =
    requestedRole === 'agent' &&
    licenseNumber.trim() &&
    licenseState.trim().length === 2 &&
    mlsBoardsList.length > 0

  const investorValid =
    requestedRole === 'investor' &&
    wholesaleIntent &&
    proofOfFundsAvailable &&
    (!needsPofFile || pofFileKey) &&
    hardMoneyLender &&
    (!needsHardMoneyExplanation || hardMoneyLenderExplanation.trim()) &&
    annualPurchaseVolume &&
    referralSource &&
    (!needsReferralDetail || referralSourceDetail.trim())

  const isFormValid = baseValid && (agentValid || investorValid)

  const isAgent = requestedRole === 'agent'
  const companyLabel = isAgent ? 'Brokerage' : 'Company Name'

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <img
            src="/images/orchard-offers.svg"
            alt="Orchard Cash Offer"
            className={styles.logo}
          />
          <Text as="h1" variant="h4" weight="bold" className={styles.title}>
            Complete Your Request
          </Text>
          <Text variant="body" className={styles.subtitle}>
            {isAgent
              ? 'Tell us about yourself and your brokerage.'
              : 'Tell us about yourself and your investment interests.'}
          </Text>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}

          <Input
            label="Email"
            type="email"
            value={email}
            disabled
          />

          <div className={styles.formRow}>
            <div className={styles.formField}>
              <Input
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>
            <div className={styles.formField}>
              <Input
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>
          </div>

          <Input
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            mask="phone"
            disabled={isSubmitting}
            required
          />

          <Input
            label={companyLabel}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            disabled={isSubmitting}
            required
          />

          <Multiselect
            label="Markets of Interest"
            value={selectedMarkets}
            onValueChange={setSelectedMarkets}
            options={groupedMarketOptions}
            disabled={isSubmitting}
          />

          {/* Qualification Questions */}
          <div className={styles.sectionDivider}>
            <Text variant="bodySm" weight="bold" className={styles.sectionLabel}>
              {isAgent ? 'Agent Qualifications' : 'Qualification Questions'}
            </Text>
          </div>

          {isAgent && (
            <>
              <Input
                label="Real Estate License Number"
                value={licenseNumber}
                onChange={(e) => setLicenseNumber(e.target.value)}
                disabled={isSubmitting}
                required
              />
              <Combobox
                source={statesSource}
                label="License State"
                value={licenseState}
                onValueChange={(value) => setLicenseState(value ?? '')}
                disabled={isSubmitting}
              />
              <Input
                label="MLS Board(s)"
                value={mlsBoardsInput}
                onChange={(e) => setMlsBoardsInput(e.target.value)}
                disabled={isSubmitting}
                placeholder="Comma-separated, e.g. ABOR, HAR"
                required
              />
              <Input
                label="How did you hear about Orchard Offers? (optional)"
                value={partnershipSource}
                onChange={(e) => setPartnershipSource(e.target.value)}
                disabled={isSubmitting}
                placeholder="e.g. Referred by Jane Smith, NAR 2026"
                maxLength={255}
              />
            </>
          )}

          {!isAgent && (
            <>
          {/* Wholesale Intent */}
          <div className={styles.fieldGroup}>
            <Text variant="bodySm" className={styles.sectionLabel}>
              Do you intend to wholesale or assign any properties once under contract?
            </Text>
            <RadioGroup value={wholesaleIntent} onValueChange={setWholesaleIntent}>
              <RadioGroup.Item value="yes" label="Yes" disabled={isSubmitting} />
              <RadioGroup.Item value="no" label="No" disabled={isSubmitting} />
              <RadioGroup.Item value="sometimes" label="Sometimes" disabled={isSubmitting} />
            </RadioGroup>
          </div>

          {/* Proof of Funds */}
          <div className={styles.fieldGroup}>
            <Text variant="bodySm" className={styles.sectionLabel}>
              Will you provide a valid proof of funds statement available for review issued within the last 30 days?
            </Text>
            <RadioGroup value={proofOfFundsAvailable} onValueChange={(val) => {
              setProofOfFundsAvailable(val)
              if (val === 'no') {
                handleRemovePofFile()
              }
            }}>
              <RadioGroup.Item value="yes" label="Yes" disabled={isSubmitting} />
              <RadioGroup.Item value="no" label="No" disabled={isSubmitting} />
            </RadioGroup>
            {needsPofFile && !pofFileKey && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handlePofFileChange}
                  disabled={isSubmitting || pofUploading}
                  style={{ display: 'none' }}
                />
                <div
                  className={styles.fileUploadArea}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Text variant="bodySm" className={styles.fileUploadText}>
                    {pofUploading ? 'Uploading...' : 'Click to upload proof of funds document (max 10MB)'}
                  </Text>
                </div>
                {pofError && <div className={styles.fileUploadError}>{pofError}</div>}
              </div>
            )}
            {pofFileKey && pofFileName && (
              <div className={styles.fileUploadAreaUploaded}>
                <span className={styles.fileUploadName}>{pofFileName}</span>
                <button
                  type="button"
                  className={styles.removeFileButton}
                  onClick={handleRemovePofFile}
                  disabled={isSubmitting}
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* Hard Money Lender */}
          <div className={styles.fieldGroup}>
            <Text variant="bodySm" className={styles.sectionLabel}>
              Do you utilize a hard money lender to finance any portion of the closing funds?
            </Text>
            <RadioGroup value={hardMoneyLender} onValueChange={(val) => {
              setHardMoneyLender(val)
              if (val === 'no') {
                setHardMoneyLenderExplanation('')
              }
            }}>
              <RadioGroup.Item value="yes" label="Yes" disabled={isSubmitting} />
              <RadioGroup.Item value="no" label="No" disabled={isSubmitting} />
              <RadioGroup.Item value="sometimes" label="Sometimes" disabled={isSubmitting} />
            </RadioGroup>
            {needsHardMoneyExplanation && (
              <Textarea
                label="Please explain"
                value={hardMoneyLenderExplanation}
                onChange={(e) => setHardMoneyLenderExplanation(e.target.value)}
                disabled={isSubmitting}
                rows={3}
              />
            )}
          </div>

          {/* Annual Purchase Volume */}
          <Select
            label="How many properties do you purchase annually?"
            value={annualPurchaseVolume}
            onValueChange={setAnnualPurchaseVolume}
            options={[
              { value: 'less_than_5', label: 'Less than 5' },
              { value: '5_to_10', label: '5-10' },
              { value: '10_to_25', label: '10-25' },
              { value: '25_to_50', label: '25-50' },
              { value: 'more_than_50', label: 'More than 50 on an annual basis' },
            ]}
            disabled={isSubmitting}
          />

          {/* Referral Source */}
          <div className={styles.fieldGroup}>
            <Select
              label="How did you hear about Orchard Offers?"
              value={referralSource}
              onValueChange={(val) => {
                setReferralSource(val)
                if (val === 'online_search') {
                  setReferralSourceDetail('')
                }
              }}
              options={[
                { value: 'online_search', label: 'Online Search' },
                { value: 'agent_referral', label: 'Agent Referral' },
                { value: 'investor_referral', label: 'Investor Referral' },
                { value: 'other', label: 'Other' },
              ]}
              disabled={isSubmitting}
            />
            {needsReferralDetail && (
              <Input
                label={referralSource === 'other' ? 'Please specify' : 'Referral name'}
                value={referralSourceDetail}
                onChange={(e) => setReferralSourceDetail(e.target.value)}
                disabled={isSubmitting}
              />
            )}
          </div>
            </>
          )}

          <Button
            type="submit"
            width="full"
            disabled={isSubmitting || !isFormValid}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </form>

        <div className={styles.linksContainer}>
          <Link to="/login" className={styles.primaryLink}>
            Already have an account? Sign in
          </Link>
        </div>
      </div>
    </div>
  )
}
